import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { 
  hasPermission, 
  hasAnyPermission as hasAnyPermissionUtil,
  canView, 
  canCreate, 
  canUpdate, 
  canDelete,
  hasFullAccess,
  hasReadOnlyAccess,
  getPermissionLevel
} from '../utils/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data.data; // Changed from response.data.user
      console.log('AuthContext - User data received:', userData); // Debug log
      setUser(userData);
      
      //console.log('AuthContext - User role:', userData.role); // Debug log
      
      // Get permissions from user's group
      if (userData.groupId) {
        try {
          const groupResponse = await api.get(`/groups/${userData.groupId}`);
          const groupPermissions = groupResponse.data.data?.permissions || [];
          setUserPermissions(groupPermissions);
          localStorage.setItem('userPermissions', JSON.stringify(groupPermissions));
        } catch (error) {
          console.error('Error fetching group permissions:', error);
          setUserPermissions([]);
        }
      } else {
        setUserPermissions([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
      setUserPermissions([]);
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, data: userData } = response.data; // Changed from user: userData
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(userData);
      
      console.log('AuthContext - Login user data:', userData); // Debug log
      console.log('AuthContext - Login user role:', userData.role); // Debug log
      
      // Get permissions from user's group
      if (userData.groupId) {
        try {
          const groupResponse = await api.get(`/groups/${userData.groupId}`);
          const groupPermissions = groupResponse.data.data?.permissions || [];
          setUserPermissions(groupPermissions);
          localStorage.setItem('userPermissions', JSON.stringify(groupPermissions));
        } catch (error) {
          console.error('Error fetching group permissions:', error);
          setUserPermissions([]);
        }
      } else {
        setUserPermissions([]);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userPermissions');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setUserPermissions([]);
  };

  // Permission checking methods using the utility functions
  const checkPermission = (requiredPermission) => {
    return hasPermission(userPermissions, requiredPermission);
  };

  const checkViewPermission = (section) => {
    return canView(userPermissions, section);
  };

  const checkCreatePermission = (section) => {
    return canCreate(userPermissions, section);
  };

  const checkUpdatePermission = (section) => {
    return canUpdate(userPermissions, section);
  };

  const checkDeletePermission = (section) => {
    return canDelete(userPermissions, section);
  };

  const checkFullAccess = (section) => {
    return hasFullAccess(userPermissions, section);
  };

  const checkReadOnlyAccess = (section) => {
    return hasReadOnlyAccess(userPermissions, section);
  };

  const getSectionPermissionLevel = (section) => {
    return getPermissionLevel(userPermissions, section);
  };

  const hasAnyPermission = (requiredPermissions) => {
    return hasAnyPermissionUtil(userPermissions, requiredPermissions);
  };

  const value = {
    user,
    loading,
    userPermissions,
    login,
    logout,
    register,
    // Permission checking methods
    checkPermission,
    checkViewPermission,
    checkCreatePermission,
    checkUpdatePermission,
    checkDeletePermission,
    checkFullAccess,
    checkReadOnlyAccess,
    getSectionPermissionLevel,
    hasAnyPermission,
    // Legacy method for backward compatibility
    hasPermission: (resource, action) => {
      console.warn('hasPermission(resource, action) is deprecated. Use checkPermission() instead.');
      return true; // Always return true for backward compatibility
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 